import {
  DEFAULT_MAX_CONCURRENT_TRANSFERS,
  DEFAULT_STUN_SERVERS,
  STORAGE_KEYS,
  userSettingsSchema,
  type UserSettings,
} from '@flowshare/protocol';
import { generateDeviceName } from '@flowshare/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface SettingsStore extends UserSettings {
  hydrated: boolean;
  setDeviceName: (name: string) => void;
  setTheme: (theme: UserSettings['theme']) => void;
  setStunServers: (servers: string[]) => void;
  setTurn: (turn: UserSettings['turn']) => void;
  setAutoAcceptTransfers: (value: boolean) => void;
  setAutoDownload: (value: boolean) => void;
  setMaxConcurrentTransfers: (value: number) => void;
  reset: () => void;
  markHydrated: (value: boolean) => void;
}

export const defaultSettings: UserSettings = {
  deviceName: generateDeviceName(),
  theme: 'system',
  stunServers: [...DEFAULT_STUN_SERVERS],
  turn: null,
  autoAcceptTransfers: true,
  autoDownload: true,
  maxConcurrentTransfers: DEFAULT_MAX_CONCURRENT_TRANSFERS,
};

function pickSettings(state: SettingsStore): UserSettings {
  return {
    deviceName: state.deviceName,
    theme: state.theme,
    stunServers: state.stunServers,
    turn: state.turn,
    autoAcceptTransfers: state.autoAcceptTransfers,
    autoDownload: state.autoDownload,
    maxConcurrentTransfers: state.maxConcurrentTransfers,
  };
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      ...defaultSettings,
      hydrated: false,
      setDeviceName: (deviceName) =>
        set({ deviceName: deviceName.trim().slice(0, 32) || generateDeviceName() }),
      setTheme: (theme) => set({ theme }),
      setStunServers: (stunServers) => set({ stunServers }),
      setTurn: (turn) => set({ turn }),
      setAutoAcceptTransfers: (autoAcceptTransfers) => set({ autoAcceptTransfers }),
      setAutoDownload: (autoDownload) => set({ autoDownload }),
      setMaxConcurrentTransfers: (maxConcurrentTransfers) =>
        set({ maxConcurrentTransfers: Math.min(8, Math.max(1, maxConcurrentTransfers)) }),
      reset: () => set({ ...defaultSettings }),
      markHydrated: (hydrated) => set({ hydrated }),
    }),
    {
      name: STORAGE_KEYS.settings,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => pickSettings(state),
      merge: (persisted, current) => {
        const parsed = userSettingsSchema.partial().safeParse(persisted);
        if (!parsed.success) return current;
        return { ...current, ...parsed.data };
      },
      onRehydrateStorage: () => (state) => {
        state?.markHydrated(true);
      },
    },
  ),
);

export function getSettingsSnapshot(): UserSettings {
  return pickSettings(useSettingsStore.getState());
}
